import React, { useState, useCallback } from 'react';
import {
    Box,
    Paper,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Button,
    Tooltip,
    Chip,
    IconButton,
    Collapse,
    Typography,
    TextField,
    Autocomplete
} from '@mui/material';
import {
    Clear as ClearIcon,
    Compare as CompareIcon,
    ExpandMore as ExpandIcon,
    ExpandLess as CollapseIcon
} from '@mui/icons-material';

/**
 * Financial Filters Component
 * 
 * Comprehensive filtering for financial dashboard with:
 * - Client filter
 * - Project filter
 * - Service line filter
 * - Contract type filter
 * - Status filter
 * - Period comparison toggle
 * 
 * @param {Object} props
 * @param {Object} props.filters - Current filter values
 * @param {Function} props.onFiltersChange - Handler for filter changes
 * @param {Array} props.clients - Available clients for dropdown
 * @param {Array} props.projects - Available projects for dropdown
 * @param {Array} props.serviceLines - Available service lines
 * @param {boolean} props.loading - Loading state
 * @param {boolean} props.showComparison - Whether comparison mode is enabled
 * @param {Function} props.onComparisonToggle - Toggle comparison mode
 */
const FinancialFilters = ({
    filters = {},
    onFiltersChange,
    clients = [],
    projects = [],
    serviceLines = [],
    loading = false,
    showComparison = false,
    onComparisonToggle,
    onExport,
    exportLoading = false
}) => {
    const [expanded, setExpanded] = useState(false);

    // Contract types
    const contractTypes = [
        { label: 'All', value: 'all' },
        { label: 'Fixed Price', value: 'lump_sum' },
        { label: 'Time & Materials', value: 'hourly' },
        { label: 'Retainer', value: 'retainer' }
    ];

    // Profitability filter
    const profitabilityOptions = [
        { label: 'All', value: 'all' },
        { label: 'Profitable', value: 'profitable' },
        { label: 'Break Even', value: 'break_even' },
        { label: 'Loss', value: 'loss' }
    ];

    // Handle filter change
    const handleFilterChange = useCallback((key, value) => {
        onFiltersChange?.({ [key]: value });
    }, [onFiltersChange]);

    // Clear all filters
    const handleClearFilters = useCallback(() => {
        onFiltersChange?.({
            contractType: 'all',
            profitability: 'all',
            clientId: null,
            serviceLine: null,
        });
    }, [onFiltersChange]);

    // Count active page-local overlay filters (portfolio scope lives on DashboardScopeStrip).
    const activeFilterCount = [
        filters.contractType !== 'all' && filters.contractType,
        filters.profitability !== 'all' && filters.profitability,
        filters.clientId,
        filters.serviceLine
    ].filter(Boolean).length;

    return (
        <Paper sx={{ p: 1.5, mb: 2 }}>
            {/* Main filter row */}
            <Box
                sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    gap: 1.5
                }}
            >
                {/* Contract type filter */}
                <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel>Contract</InputLabel>
                    <Select
                        value={filters.contractType || 'all'}
                        label="Contract"
                        onChange={(e) => handleFilterChange('contractType', e.target.value)}
                    >
                        {contractTypes.map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {/* Comparison toggle */}
                <Tooltip title={showComparison ? 'Hide period comparison' : 'Compare with prior period'}>
                    <Button
                        size="small"
                        variant={showComparison ? 'contained' : 'outlined'}
                        startIcon={<CompareIcon />}
                        onClick={onComparisonToggle}
                        sx={{ fontSize: '0.75rem' }}
                    >
                        Compare
                    </Button>
                </Tooltip>

                {/* Expand/collapse more filters */}
                <Tooltip title={expanded ? 'Fewer filters' : 'More filters'}>
                    <IconButton
                        size="small"
                        onClick={() => setExpanded(!expanded)}
                        color={activeFilterCount > 0 ? 'primary' : 'default'}
                    >
                        {activeFilterCount > 0 && (
                            <Chip
                                label={activeFilterCount}
                                size="small"
                                color="primary"
                                sx={{
                                    height: 16,
                                    fontSize: '0.65rem',
                                    position: 'absolute',
                                    top: -4,
                                    right: -4
                                }}
                            />
                        )}
                        {expanded ? <CollapseIcon /> : <ExpandIcon />}
                    </IconButton>
                </Tooltip>

                {/* Clear filters */}
                {activeFilterCount > 0 && (
                    <Tooltip title="Clear all filters">
                        <IconButton size="small" onClick={handleClearFilters}>
                            <ClearIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}

                {/* Spacer */}
                <Box sx={{ flexGrow: 1 }} />

                {/* Export button */}
                {onExport && (
                    <Button
                        size="small"
                        variant="outlined"
                        onClick={() => onExport('CSV')}
                        disabled={exportLoading}
                        sx={{ fontSize: '0.75rem' }}
                    >
                        Export CSV
                    </Button>
                )}
            </Box>

            {/* Expanded filters */}
            <Collapse in={expanded}>
                <Box
                    sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 1.5,
                        mt: 1.5,
                        pt: 1.5,
                        borderTop: '1px solid',
                        borderColor: 'divider'
                    }}
                >
                    {/* Client filter */}
                    {clients.length > 0 && (
                        <Autocomplete
                            size="small"
                            options={clients}
                            getOptionLabel={(option) => option.name || option.client_name || ''}
                            value={clients.find(c => c.id === filters.clientId) || null}
                            onChange={(_, value) => handleFilterChange('clientId', value?.id || null)}
                            renderInput={(params) => (
                                <TextField {...params} label="Client" sx={{ minWidth: 180 }} />
                            )}
                            sx={{ minWidth: 180 }}
                        />
                    )}

                    {/* Service line filter */}
                    {serviceLines.length > 0 && (
                        <FormControl size="small" sx={{ minWidth: 150 }}>
                            <InputLabel>Service Line</InputLabel>
                            <Select
                                value={filters.serviceLine || ''}
                                label="Service Line"
                                onChange={(e) => handleFilterChange('serviceLine', e.target.value || null)}
                            >
                                <MenuItem value="">All</MenuItem>
                                {serviceLines.map((sl) => (
                                    <MenuItem key={sl.id || sl} value={sl.id || sl}>
                                        {sl.name || sl}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}

                    {/* Profitability filter */}
                    <FormControl size="small" sx={{ minWidth: 130 }}>
                        <InputLabel>Profitability</InputLabel>
                        <Select
                            value={filters.profitability || 'all'}
                            label="Profitability"
                            onChange={(e) => handleFilterChange('profitability', e.target.value)}
                        >
                            {profitabilityOptions.map((opt) => (
                                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>
            </Collapse>
        </Paper>
    );
};

export default FinancialFilters;

